import { useState } from 'react';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { Box, Button, Card, Flex, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { CriticalSystemInfo } from '@/utils/criticalInfoUtils';
import { FiPlus } from 'react-icons/fi';
import { CriticalSystemType, CriticalSystemTypes } from '@/types/CriticalInfo.ts';
import { preventScrolling } from '@/utils/preventScrolling.ts';
import { CONTROL_SURFACE_SHADOW } from '@/components/ui/shadows';

const SYSTEM_PILL_HEIGHT = { base: 10, md: 9 } as const;
const SYSTEM_PILL_PADDING_START = { base: 2, md: 2 } as const;
const SYSTEM_PILL_PADDING_END = { base: 3, md: 3 } as const;

/**
 * Component for adding new systems to the important information.
 * Provides both quick-add buttons for common systems and a custom input
 * for adding user-defined systems, enhancing flexibility and completeness.
 */
interface AddSystemSectionProps {
	availableSystems: CriticalSystemInfo[];
	existingSystems: string[];
	onAddSystem: (name: string, type: CriticalSystemType) => void;
}

const AddSystemSection = ({ existingSystems, availableSystems, onAddSystem }: AddSystemSectionProps) => {
	const [newSystemName, setNewSystemName] = useState('');
	const [showMoreSuggestions, setShowMoreSuggestions] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Handles adding a custom system.
	 * Validates the input to prevent duplicates and empty names,
	 * providing immediate feedback to improve the user experience.
	 */
	const handleAddCustomSystem = () => {
		const trimmedName = newSystemName.trim();
		if (!trimmedName) return;

		if (existingSystems.includes(trimmedName)) {
			setError('This system already exists');
			return;
		}

		onAddSystem(trimmedName, CriticalSystemTypes.Custom);
		setNewSystemName('');
		setError(null);
	};

	const displayedSystems = availableSystems.filter((system) => {
		return showMoreSuggestions ? true : system.showByDefault;
	});
	return (
		<Card.Root layerStyle="card">
			<Card.Body p={5}>
				<VStack gap={4} align="stretch">
					<Flex justify="space-between" align="center" gap={3}>
						<Text textStyle="body.strong">Add more</Text>
						{!showMoreSuggestions && availableSystems.some((system) => !system.showByDefault) && (
							<Button
								variant="ghost"
								size="sm"
								color="fg.muted"
								_hover={{ bg: 'surface.subtle' }}
								borderRadius="full"
								onMouseDown={preventScrolling}
								onClick={() => setShowMoreSuggestions(true)}
							>
								More suggestions
							</Button>
						)}
					</Flex>

					{!!availableSystems.length && (
						<Flex flexWrap="wrap" gap={2}>
							{displayedSystems.map((system) => (
								<OutlinedButton
									elevated
									key={system.name}
									size="sm"
									h={SYSTEM_PILL_HEIGHT}
									minW="auto"
									ps={SYSTEM_PILL_PADDING_START}
									pe={SYSTEM_PILL_PADDING_END}
									gap={1.5}
									onMouseDown={preventScrolling}
									onClick={() =>
										onAddSystem(system.name, system.type ?? CriticalSystemTypes.Suggested)
									}
								>
									<FiPlus size={14} />
									<Text textStyle="label">{system.name}</Text>
								</OutlinedButton>
							))}
						</Flex>
					)}

					{showMoreSuggestions && (
						<Box textAlign="center">
							<Button
								variant="ghost"
								size="sm"
								color="fg.subtle"
								_hover={{ bg: 'surface.subtle' }}
								borderRadius="full"
								onMouseDown={preventScrolling}
								onClick={() => setShowMoreSuggestions(false)}
							>
								Show less
							</Button>
						</Box>
					)}

					{availableSystems.length > 0 && <Box h="1px" bg="border.default" />}

					<VStack gap={2} align="stretch">
						<Text textStyle="label">Custom</Text>
						<HStack gap={2} minW={0}>
							<Input
								borderColor={error ? 'red.400' : 'border.default'}
								bg="surface.default"
								flex="1"
								minW={0}
								h={10}
								borderRadius="full"
								textOverflow="ellipsis"
								placeholder="e.g. Generator, Solar Inverter"
								_placeholder={{ color: 'fg.subtle' }}
								transition="border-color 0.15s ease"
								_hover={{
									'&:not(:disabled):not([readonly]):not([aria-disabled=true]):not(:focus):not(:focus-within)':
										{
											borderColor: error ? 'red.400' : 'border.strong',
										},
								}}
								_focus={{ borderColor: error ? 'red.400' : 'transparent' }}
								focusRingWidth="1px"
								focusRingColor={error ? 'red.400' : 'focus.ring'}
								value={newSystemName}
								onChange={(e) => {
									setNewSystemName(e.target.value);
									if (error) setError(null);
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault();
										handleAddCustomSystem();
									}
								}}
							/>
							<Button
								h={10}
								minW={10}
								w={10}
								p={0}
								onClick={handleAddCustomSystem}
								disabled={!newSystemName.trim()}
								bg="brand.solid"
								boxShadow={CONTROL_SURFACE_SHADOW}
								color="brand.contrast"
								_hover={{ bg: 'brand.solidHover' }}
								borderRadius="full"
								_disabled={{ cursor: 'default', opacity: 0.45 }}
							>
								<FiPlus />
							</Button>
						</HStack>
						{error && (
							<Text textStyle="body.sm" color="text.error" mt={2} ml={3}>
								{error}
							</Text>
						)}
					</VStack>
				</VStack>
			</Card.Body>
		</Card.Root>
	);
};

export default AddSystemSection;
